import { createApp } from 'vue';
import { createPinia } from 'pinia';

import ElementPlus, { ElNotification } from 'element-plus';
import { createI18n } from 'vue-i18n';
import './capacitors/init';
import { logError } from '@/utils/logger';
import locales from './locales';
import * as core from '@posx/core';
import { init as sentryVueInit, BrowserTracing, replayIntegration } from '@sentry/vue';
import { i18n as JSONSchamaI18n } from '@lljj/vue3-form-element';
// @xpos/ui components stay here: their styles must load before Tailwind/Element Plus
import XDialog from '@xpos/ui/src/components/Dialog.vue';
import XItem from '@xpos/ui/src/components/Item.vue';
import XInput from '@xpos/ui/src/components/Input.vue';
import XButton from '@xpos/ui/src/components/Button.vue';
import XKeyboard from '@xpos/ui/src/components/KeyBoard.vue';
import XLabel from '@xpos/ui/src/components/Label.vue';
import localizeEn from 'ajv-i18n/localize/en';
import { eventBus } from '@/libs/EventBus';
import VueTelInput from 'vue-tel-input';
import { IonicVue } from '@ionic/vue';
import 'vue-tel-input/vue-tel-input.css';

import App from './App.vue';
import router from './routes';

import '@xpos/ui/src/global.scss';
import 'element-plus/dist/index.css';
import '@xpos/ui/src/styles/element-var.scss';
import '/public/iconfont/iconfont.js';


// import { getCapacitorDriver } from './capacitors/driver/capacitor-sqlite';
// import { config } from '@posx/core';
import registerComponents from './plugins/registerComponents';
// import secureStoragePlugin from './plugins/secure.storage.plugin';
console.log('app start');
const app = createApp(App);
const pinia = createPinia();

const defaultLocale = localStorage.getItem('default_locale') || 'en';
export const i18n = createI18n({
  legacy: false,
  locale: defaultLocale, // set locale
  fallbackLocale: 'en', // hard fallback so unsupported locale codes still render English text
  messages: {
    en: locales.en,
    zh: locales.zh,
    vi: locales.vi,
    id: locales.id,
  },
});
eventBus.on('localeChange', (locale: any) => {
  i18n.global.locale.value = locale;
  localStorage.setItem('default_locale', locale);
});

app.component('XButton', XButton);
app.component('XDialog', XDialog);
app.component('XInput', XInput);
app.component('XItem', XItem);
app.component('XKeyboard', XKeyboard);
app.component('XLabel', XLabel);
app.use(registerComponents);
app.use(pinia);
app.use(ElementPlus);
app.use(router);
app.use(i18n);
app.use(VueTelInput);
app.use(IonicVue, {
  mode: 'ios',
});

JSONSchamaI18n.useLocal(localizeEn);

// Prevent scroll wheel from changing number input values
document.addEventListener('wheel', ({ target }) => {
  if ((target as HTMLInputElement).type === 'number') (target as HTMLInputElement).blur()
})

// Only initialize Sentry (JS-only @sentry/vue, no native plugin so CodePush updates work on all devices)
if (import.meta.env.VITE_IS_PROD === 'true') {
  sentryVueInit({
    app,
    dsn: 'https://77a5b7a0b37212487499e0157613e18a@o102090.ingest.sentry.io/4506702926381056',
    integrations: [
      new BrowserTracing({ tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/] }),
      replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    ignoreErrors: [
      /^system_error:/,
      'ResizeObserver loop completed with undelivered notifications.',
      'ResizeObserver loop limit exceeded',
      /cursor.*doesn't exist/i,
      /Unable To Obtain Valid Token/i,
      /Handshake was canceled/i,
      /Cannot send data/i,
      /Failed to complete negotiation/i, // SignalR auto-reconnect on offline/weak network (XPOS-1XD)
      /reading 'toUpperCase'/i,
      /scaleMode/i, // Telerik ReportViewer internal state race (XPOS-1FC)
      'Error shown. Throwing promises chain stop error.', // Telerik ReportViewer internal promise chain control
      'No report instance.', // Telerik ReportViewer Stop Rendering before instance ready (XPOS-22C)
      /weixinPostMessageHandlers/, // WeChat in-app browser JSBridge call on beforeunload, not app code (XPOS-22N)
      /Client with ID '\w+' not found\. Expired/, // Telerik session expiry, auto-refreshed by ReportViewer handleUnhandledRejection (XPOS-1X2)
    ],
    beforeSend(event: any, hint: any) {
      const error = hint.originalException as any;
      if (error === 'cancel' || error === 'close') return null; // ElMessageBox.confirm cancel/close
      if (error === 'Connection stopped.') return null; // SignalR TransportSendQueue rejects queued sends with this string when the connection stops (XPOS-23C)
      if (error?.code === 'ERR_NETWORK' && !error?.response) return null;
      if (error?.message?.match(/hostname could not be found/i)) {
        event.level = 'info';
        event.tags = { ...event.tags, dns_failure: true };
        return event;
      }
      if (error?.message?.match(/Connection to Indexed Database server lost/i)) {
        logError('IndexedDB connection lost (Sentry suppressed)', { name: error?.name, message: error?.message });
        return null;
      }
      const redisTimeoutRx = /Timeout performing \w+.*(Xpos\.Report\.Server|redis\.revopos\.io)|Invalid clientID$/i; // Server-side Redis timeout / Telerik client creation failure, both handled by ReportViewer (XPOS-225/208/1MT)
      if (event.exception?.values?.some((v: any) => redisTimeoutRx.test(v.value || '') || redisTimeoutRx.test(v.type || '')) || redisTimeoutRx.test(event.message || '')) {
        logError('Server Redis timeout leak (Sentry suppressed)', { message: event.message, exception: event.exception });
        return null;
      }
      return event;
    },
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// datadogLogs.init({
//   clientToken: 'pub5338f45015cc202c864f66a23c2faf9d',
//   site: 'us5.datadoghq.com',
//   forwardErrorsToLogs: true,
//   sessionSampleRate: 100,
//   service: 'xpos',
//   // get version from package.json
//   version: packageJson.version,
//   // beforeSend: (log) => {
//   //   log.device_id = secureStorage.getItem('device_uid') || 'unknown';
//   //   log.merchant_id = secureStorage.getItem('merchant_uid') || 'unknown';
//   //   log.environment = import.meta.env.VITE_IS_PROD == 'true' ? 'production' : 'development';
//   //   return true;
//   // }
// });

// app.config.globalProperties.$config = {
//   colorPrimary: import.meta.env.VITE_SCSS_COLOR_PRIMARY,
//   colorSuccess: import.meta.env.VITE_SCSS_COLOR_GREEN,
// };

// Expose Receipt component globally for CRM package (stores initialized in App.vue onMounted)
import Receipt from './views/pos/table/components/Receipt.vue';
import secureStorage from '@xpos/utils/src/secure.storage.utils';

(window as any).__RECEIPT_COMPONENT__ = Receipt;

// Handle URL parameters for merchant switching
const handleUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const merchantUid = urlParams.get('merchant_uid');
  const token = urlParams.get('token');
  if (merchantUid && token) {
    // Switch handoff: purge stale target-subdomain state so authCallback re-registers cleanly
    ['device_uid', 'api_key', 'vault'].forEach(k => secureStorage.removeItem(k));
    localStorage.removeItem('is_sync_completed');
    localStorage.setItem('cap_sec_token_response', token);
    secureStorage.setItem('merchant_uid', merchantUid);
  }
};

// Handle URL parameters before mounting the app
handleUrlParams();

// Notify and auto-refresh once on stale chunk/CSS preload failures; error still propagates so Sentry sees the real cause
window.addEventListener('vite:preloadError', () => {
  ElNotification({ customClass: 'x-notification__info', title: i18n.global.t('common.info'), message: i18n.global.t('common.new_version_refresh') })
  const key = 'vite_preload_error_refreshed'
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1')
    window.location.reload()
  }
})

app.mount('#app');
